// Tesla Timeline Viewer Types v1.0.0

// Camera channels (Tesla dashcam positions)
export type CameraChannel =
  | 'front'
  | 'back'
  | 'left_repeater'
  | 'right_repeater'
  | 'front_fisheye'  // Some models have cabin camera
  | 'narrow_front';   // Some models have narrow front

export const CAMERA_LABELS: Record<CameraChannel, string> = {
  front: 'Front',
  back: 'Rear',
  left_repeater: 'Left',
  right_repeater: 'Right',
  front_fisheye: 'Cabin',
  narrow_front: 'Narrow',
};

// Clip event types from Tesla
export type ClipEventType =
  | 'SentryMode'
  | 'SavedClip'
  | 'RecentClips'
  | 'Honk'
  | 'UserInteraction';

// A single video clip segment
export interface VideoClip {
  id: string;
  channel: CameraChannel;
  eventType: ClipEventType;
  startTime: Date;
  endTime: Date;
  duration: number; // seconds
  url: string; // R2 signed URL
  thumbnailUrl?: string;
  fileSize: number;
  hasAudio: boolean;
  // AI analysis results
  analysis?: ClipAnalysis;
}

// AI analysis attached to a clip
export interface ClipAnalysis {
  objects: Detection[];
  plates: PlateDetection[];
  scene?: SceneDescription;
  tamperingIndicators?: TamperingIndicator[];
  threatLevel: 'none' | 'low' | 'medium' | 'high' | 'critical';
  hasChildDetection: boolean;
  childMaskRequired: boolean;
}

// Object detection with bounding box
export interface Detection {
  id: string;
  label: string;
  confidence: number;
  box: BoundingBox;
  timestamp: number; // seconds into clip
  isChild: boolean;
  trackingId?: string; // For tracking same object across frames
}

export interface BoundingBox {
  x: number;      // 0-1 normalized
  y: number;      // 0-1 normalized
  width: number;  // 0-1 normalized
  height: number; // 0-1 normalized
}

export interface PlateDetection {
  plate: string;
  confidence: number;
  box: BoundingBox;
  timestamp: number;
  region?: string;
  isFlagged: boolean;
  flagReason?: string;
}

export interface SceneDescription {
  description: string;
  environment: string;
  timeOfDay: string;
  weather?: string;
  activityLevel: string;
}

export interface TamperingIndicator {
  type: string;
  confidence: number;
  description: string;
  timestamp: number;
}

// Timeline segment (continuous recording period)
export interface TimelineSegment {
  id: string;
  startTime: Date;
  endTime: Date;
  eventType: ClipEventType;
  channels: CameraChannel[];
  clips: VideoClip[];
  hasThreat: boolean;
  hasChildContent: boolean;
}

// Child mask authorization
export interface MaskAuthorization {
  id: string;
  requestedAt: Date;
  requestedBy: string;
  reason: string;
  clipId: string;
  status: 'pending' | 'approved' | 'denied' | 'expired';
  approvedAt?: Date;
  approvedBy?: string;
  expiresAt?: Date;
  authCode?: string;
}

// Viewer state
export interface ViewerState {
  // Time navigation
  currentTime: Date;
  playbackSpeed: number;
  isPlaying: boolean;

  // Channel selection
  selectedChannels: CameraChannel[];
  layout: 'single' | 'grid' | 'pip'; // Picture-in-picture
  primaryChannel: CameraChannel;

  // Timeline zoom
  timelineStart: Date;
  timelineEnd: Date;
  zoomLevel: number; // 1 = 24 hours, 2 = 12 hours, etc.

  // Filters
  eventTypeFilter: ClipEventType[];
  threatLevelFilter: ('none' | 'low' | 'medium' | 'high' | 'critical')[];
  showOnlyWithDetections: boolean;

  // Child mask
  maskChildDetections: boolean;
  activeMaskAuthorizations: MaskAuthorization[];
}

// API response types
export interface TimelineResponse {
  segments: TimelineSegment[];
  totalClips: number;
  dateRange: {
    earliest: string;
    latest: string;
  };
}

export interface ClipStreamResponse {
  url: string;
  expiresAt: string;
  requiresMask: boolean;
  maskRegions?: BoundingBox[];
}

// Detection overlay frame (for rendering AI detections)
export interface DetectionFrame {
  timestamp: number;
  detections: Detection[];
  plates: PlateDetection[];
  childMasks: BoundingBox[]; // Areas to mask
}

// Event markers on timeline
export interface TimelineMarker {
  time: Date;
  type: 'motion' | 'person' | 'vehicle' | 'threat' | 'plate' | 'child';
  label?: string;
  severity?: 'info' | 'warning' | 'danger';
}
